import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const bookingResource = (token: string) => `/public/booking/${encodeURIComponent(token)}`;

export interface PublicBookingReviewSummary {
  average: number;
  count: number;
}

export interface PublicBookingClubPlanPreview {
  id: number;
  name: string;
  price: number;
  monthly_visit_limit?: number | null;
}

export interface PublicBookingClubTeaser {
  has_plans: boolean;
  plans_url: string | null;
  join_url: string | null;
  plans_preview?: PublicBookingClubPlanPreview[];
}

export interface PublicBookingMetadata {
  is_powered_by_adatis: boolean;
  review_summary?: PublicBookingReviewSummary | null;
  club_teaser?: PublicBookingClubTeaser | null;
}

export interface PublicBookingServiceCategory {
  name: string;
}

export interface PublicBookingService {
  id: number;
  name: string;
  description?: string | null;
  duration_minutes: number;
  price_cents: number;
  list_price_cents?: number;
  list_price_display?: string;
  promo_active?: boolean;
  price_is_from?: boolean;
  price_display: string;
  cover_url: string | null;
  category?: PublicBookingServiceCategory;
}

export interface PublicBookingProfessional {
  id: number;
  name: string;
  color_hex: string;
  photo_url: string | null;
}

export interface PublicBookingAvailabilitySlot {
  start_datetime: string;
  end_datetime: string;
}

export interface PublicBookingCustomerPayload {
  name: string;
  country_code: string;
  phone_number: string;
  email?: string;
}

export interface CreatePublicBookingAppointmentPayload extends PublicBookingCustomerPayload {
  service_id: number;
  professional_id: number;
  start_datetime: string;
  end_datetime?: string;
}

export interface PublicBookingAppointment {
  id: number;
  start_datetime: string;
  end_datetime: string;
  status: string;
  professional?: { id: number; name: string; color_hex?: string };
  service?: { id: number; name: string; duration_minutes?: number };
  client?: { id: number; name: string; phone_number?: string; email?: string | null };
}

export interface PublicBookingServicesResponse extends ApiResponse<PublicBookingService[]> {
  _metadata?: PublicBookingMetadata;
}

export interface CreatePublicBookingAppointmentResponse extends ApiResponse<PublicBookingAppointment> {
  _metadata?: PublicBookingMetadata;
}

export const getPublicBookingServices = (
  token: string
): Promise<PublicBookingServicesResponse> =>
  api.get<PublicBookingServicesResponse>(`${bookingResource(token)}/services`);

export const getPublicBookingProfessionals = (
  token: string,
  params?: { service_id?: number }
): Promise<ApiResponse<PublicBookingProfessional[]>> =>
  api.get<ApiResponse<PublicBookingProfessional[]>>(
    `${bookingResource(token)}/professionals`,
    params
  );

export const getPublicBookingAvailability = (
  token: string,
  professionalId: number,
  params: { date: string; service_id: number }
): Promise<ApiResponse<PublicBookingAvailabilitySlot[]>> =>
  api.get<ApiResponse<PublicBookingAvailabilitySlot[]>>(
    `${bookingResource(token)}/professionals/${professionalId}/availability`,
    params
  );

export const createPublicBookingAppointment = (
  token: string,
  payload: CreatePublicBookingAppointmentPayload
): Promise<CreatePublicBookingAppointmentResponse> =>
  api.create<CreatePublicBookingAppointmentResponse>(
    `${bookingResource(token)}/appointments`,
    payload
  );

export interface PublicBookingNextSlot {
  starts_at_utc: string;
  ends_at_utc: string;
  local_label: string;
}

export interface PublicBookingNextSlotData {
  slot: PublicBookingNextSlot | null;
  book_url: string;
  copy: string;
}

export const getPublicBookingNextSlot = (
  token: string,
  params?: { service_id?: number }
): Promise<ApiResponse<PublicBookingNextSlotData>> =>
  api.get<ApiResponse<PublicBookingNextSlotData>>(
    `${bookingResource(token)}/next-slot`,
    params
  );
